import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  ComputerDesktopIcon,
  CameraIcon,
  ArrowRightIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

export const ProductCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const products = [
    {
      id: 1,
      name: "DICOMFiles",
      tagline: "Automatización Completa del Flujo DICOM",
      description: "Sistema avanzado que reduce el trabajo manual en un 80-90% y elimina errores humanos en la conversión de documentos DICOM.",
      icon: <ComputerDesktopIcon className="h-16 w-16" />,
      features: [
        "Procesamiento 24/7 sin intervención humana",
        "Integración transparente con sistemas PACS",
        "Gestión inteligente de worklist DICOM",
        "Procesamiento automático de PDFs",
        "Sistema de códigos QR integrado"
      ],
      link: "/dicomfiles",
      cta: "Conocer DICOMFiles",
      gradient: "from-blue-500 to-blue-700",
      bgGradient: "from-blue-50 to-indigo-100"
    },
    {
      id: 2,
      name: "DICOMCapture",
      tagline: "Captura y Procesamiento Avanzado",
      description: "Herramienta especializada para la captura y procesamiento de imágenes DICOM desde equipos médicos con validación automática.",
      icon: <CameraIcon className="h-16 w-16" />,
      features: [
        "Captura desde equipos médicos",
        "Validación automática de calidad",
        "Procesamiento en tiempo real",
        "Integración con sistemas PACS",
        "Control de flujo de trabajo"
      ],
      link: "/contact",
      cta: "Más Información",
      gradient: "from-green-500 to-green-700",
      bgGradient: "from-green-50 to-emerald-100"
    }
  ];

  // Auto-advance slides every 8 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % products.length);
    }, 8000);

    return () => clearInterval(timer);
  }, [products.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + products.length) % products.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % products.length);
  };

  return (
    <div className="relative w-full h-[600px] overflow-hidden rounded-2xl shadow-2xl">
      {/* Slides */}
      <div 
        className="flex transition-transform duration-500 ease-in-out h-full"
        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
      >
        {products.map((product) => (
          <div
            key={product.id}
            className={`w-full h-full flex-shrink-0 bg-gradient-to-br ${product.bgGradient} relative`}
          >
            <div className="absolute inset-0 bg-black bg-opacity-10"></div>
            <div className="relative z-10 h-full flex items-center">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  {/* Content */}
                  <div className="text-white">
                    <div className="mb-6">
                      <div className="text-white mb-4 flex items-center">
                        {product.icon}
                      </div>
                      <h2 className="text-4xl md:text-5xl font-bold mb-4">
                        {product.name}
                      </h2>
                      <p className="text-xl md:text-2xl font-semibold mb-4 text-white/90">
                        {product.tagline}
                      </p>
                      <p className="text-lg text-white/80 mb-6">
                        {product.description}
                      </p>
                    </div>

                    {/* Features */}
                    <div className="mb-8">
                      <ul className="space-y-3">
                        {product.features.slice(0, 3).map((feature, index) => (
                          <li key={index} className="flex items-center">
                            <CheckIcon className="h-5 w-5 text-white mr-3 flex-shrink-0" />
                            <span className="text-white/90">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* CTA */}
                    <div className="flex items-center">
                      <Link
                        to={product.link}
                        className="bg-white text-gray-900 px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors duration-200 flex items-center"
                      >
                        {product.cta}
                        <ArrowRightIcon className="ml-2 h-5 w-5" />
                      </Link>
                    </div>
                  </div>

                  {/* Visual Element */}
                  <div className="hidden lg:block">
                    <div className={`bg-gradient-to-br ${product.gradient} rounded-2xl p-8 shadow-2xl`}>
                      <div className="text-center text-white">
                        <div className="mb-6 flex justify-center">
                          <div className="h-24 w-24 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                            {product.icon}
                          </div>
                        </div>
                        <h3 className="text-2xl font-bold mb-4">{product.name}</h3>
                        <div className="space-y-2">
                          {product.features.slice(0, 2).map((feature, index) => (
                            <div key={index} className="text-white/90 text-sm">
                              ✓ {feature}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-3 rounded-full transition-all duration-200 z-20"
        aria-label="Slide anterior"
      >
        <ChevronLeftIcon className="h-6 w-6" />
      </button>
      
      <button
        onClick={goToNext}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-3 rounded-full transition-all duration-200 z-20"
        aria-label="Siguiente slide"
      >
        <ChevronRightIcon className="h-6 w-6" />
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
        {products.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-200 ${
              index === currentSlide 
                ? 'bg-white' 
                : 'bg-white bg-opacity-50 hover:bg-opacity-75'
            }`}
            aria-label={`Ir al slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 h-1 bg-white bg-opacity-20 w-full z-20">
        <div 
          className="h-full bg-white transition-all duration-100 ease-linear"
          style={{ 
            width: `${((currentSlide + 1) / products.length) * 100}%` 
          }}
        />
      </div>
    </div>
  );
};
